import React, { useState, useMemo, useEffect } from 'react';
import { useAllInvoices, useAllOrders } from '../../hooks/useAdminData';
import { massLabel } from '../../utils/massCatalog';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLORS, SHADOWS } from '../../styles/tokens';

const PERIOD_OPTIONS = [
  { value: 7,   label: 'Siste 7 dager' },
  { value: 30,  label: 'Siste 30 dager' },
  { value: 90,  label: 'Siste 90 dager' },
  { value: 365, label: 'Siste 12 måneder' },
  { value: 0,   label: 'All tid' },
];

export default function RevenueOverview() {
  const { invoices, loading: invLoading } = useAllInvoices();
  const { orders,   loading: ordLoading } = useAllOrders();
  const [days, setDays] = useState(30);
  const [pricingSettings, setPricingSettings] = useState(null);

  useEffect(() => {
    getDoc(doc(db, 'settings', 'pricing'))
      .then(snap => { if (snap.exists()) setPricingSettings(snap.data()); })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!days) return invoices;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return invoices.filter(inv => {
      const d = inv.createdAt?.toDate?.();
      return d && d >= cutoff;
    });
  }, [invoices, days]);

  const totalRevenue  = filtered.reduce((s, i) => s + (i.tippiFee    ?? 0), 0);
  const totalPayout   = filtered.reduce((s, i) => s + (i.driverPayout ?? 0), 0);
  const totalTurnover = filtered.reduce((s, i) => s + (i.totalAmount  ?? 0), 0);
  const avgOrder      = filtered.length ? Math.round(totalTurnover / filtered.length) : 0;

  // Ordrestatus-fordeling
  const statusCounts = useMemo(() => {
    const counts = {};
    orders.forEach(o => { counts[o.status] = (counts[o.status] ?? 0) + 1; });
    return counts;
  }, [orders]);

  if (invLoading || ordLoading) return <p style={{ padding: 24, color: COLORS.textLight }}>Laster data…</p>;

  return (
    <div style={styles.page}>
      {/* Period-filter */}
      <div style={styles.toolbar}>
        <h2 style={styles.title}>Inntektsoversikt</h2>
        <select
          style={styles.select}
          value={days}
          onChange={e => setDays(Number(e.target.value))}
        >
          {PERIOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Kalibreringsstatus */}
      {pricingSettings && (
        <div style={{ ...styles.card, display: 'flex', gap: 32, alignItems: 'center', marginBottom: 16, padding: '14px 24px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: COLORS.textLight, fontWeight: 700, textTransform: 'uppercase' }}>Priskalibering</span>
          <span style={{ fontSize: 14 }}>Gjeldende sats: <strong>kr {pricingSettings.baseRatePerKm}/km</strong></span>
          <span style={{ fontSize: 14 }}>Sist kalibrert: <strong>
            {pricingSettings.lastCalibratedAt
              ? pricingSettings.lastCalibratedAt.toDate().toLocaleDateString('nb-NO', { dateStyle: 'medium' })
              : 'Aldri'}
          </strong></span>
          <span style={{ fontSize: 14 }}>Datagrunnlag: <strong>{pricingSettings.sampleSize ?? '—'} ordre</strong></span>
        </div>
      )}

      {/* KPI-kort */}
      <div style={styles.kpiGrid}>
        <KpiCard label="Tippi-inntekt"     value={totalRevenue}  color={COLORS.danger} note="10% gebyr" />
        <KpiCard label="Total omsetning"   value={totalTurnover} color={COLORS.textDark} />
        <KpiCard label="Utbetalt sjåfører" value={totalPayout}   color={COLORS.success} />
        <KpiCard label="Gj.snitt per ordre" value={avgOrder}     color={COLORS.purple} note={`${filtered.length} fakturaer`} />
      </div>

      {/* Ordrestatus-oversikt */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Alle oppdrag — status</h3>
        <div style={styles.statusGrid}>
          {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
            <StatusPill
              key={status}
              label={cfg.label}
              count={statusCounts[status] ?? 0}
              color={cfg.color}
            />
          ))}
        </div>
      </div>

      {/* Fakturatabell */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>
          Fakturaer ({filtered.length})
        </h3>

        {filtered.length === 0 ? (
          <p style={{ color: COLORS.textLight, fontSize: 14 }}>Ingen fakturaer i valgt periode</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Ordre', 'Dato', 'Massetype', 'Grunnbeløp', 'Justering', 'Tippi-gebyr', 'Sjåfør', 'Total', 'Status'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const date = inv.createdAt?.toDate?.();
                  const order = orders.find(o => o.id === inv.orderId);
                  return (
                    <tr key={inv.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={styles.td}>#{inv.orderId?.slice(-6)}</td>
                      <td style={styles.td}>
                        {date?.toLocaleDateString('nb-NO', { dateStyle: 'short' }) ?? '—'}
                      </td>
                      <td style={styles.td}>
                        {massLabel(order?.massType) ?? '—'}
                      </td>
                      <td style={styles.td}>kr {inv.baseAmount?.toLocaleString('nb-NO')},-</td>
                      <td style={{ ...styles.td, color: (inv.adjustmentAmount ?? 0) > 0 ? COLORS.danger : (inv.adjustmentAmount ?? 0) < 0 ? COLORS.success : COLORS.textLight }}>
                        {inv.adjustmentAmount !== 0 ? `${inv.adjustmentAmount > 0 ? '+' : ''}kr ${inv.adjustmentAmount},-` : '—'}
                      </td>
                      <td style={{ ...styles.td, color: COLORS.danger, fontWeight: 600 }}>
                        kr {inv.tippiFee?.toLocaleString('nb-NO')},-
                      </td>
                      <td style={{ ...styles.td, color: COLORS.success }}>
                        kr {inv.driverPayout?.toLocaleString('nb-NO')},-
                      </td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        kr {inv.totalAmount?.toLocaleString('nb-NO')},-
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: inv.status === 'captured' ? COLORS.success : COLORS.warning }}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: COLORS.surfaceHover, fontWeight: 700 }}>
                  <td style={styles.td} colSpan={5}>Sum</td>
                  <td style={{ ...styles.td, color: COLORS.danger }}>kr {totalRevenue.toLocaleString('nb-NO')},-</td>
                  <td style={{ ...styles.td, color: COLORS.success }}>kr {totalPayout.toLocaleString('nb-NO')},-</td>
                  <td style={styles.td}>kr {totalTurnover.toLocaleString('nb-NO')},-</td>
                  <td style={styles.td} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Hjelpere ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft:       { label: 'Utkast',          color: COLORS.textLight },
  open:        { label: 'Åpen',            color: COLORS.blue },
  matched:     { label: 'Tildelt',         color: COLORS.purple },
  in_progress: { label: 'Under transport', color: COLORS.warning },
  completed:   { label: 'Fullført',        color: COLORS.success },
  invoiced:    { label: 'Fakturert',       color: '#16a085' },
  disputed:    { label: 'Tvist',           color: COLORS.danger },
};

function KpiCard({ label, value, color, note }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ fontSize: 12, color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{`kr ${value.toLocaleString('nb-NO')},-`}</div>
      {note && <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>{note}</div>}
    </div>
  );
}

function StatusPill({ label, count, color }) {
  return (
    <div style={styles.statusPill}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>{label}</div>
    </div>
  );
}

const styles = {
  page:        { padding: '24px 28px', overflowY: 'auto', height: '100%' },
  toolbar:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title:       { fontSize: 20, fontWeight: 700, color: COLORS.textDark, margin: 0 },
  select:      { padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 14, background: COLORS.white, color: COLORS.textDark },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 },
  kpiCard:     { background: COLORS.white, borderRadius: 10, padding: '20px 20px', boxShadow: SHADOWS.sm },
  card:        { background: COLORS.white, borderRadius: 10, padding: '20px 24px', marginBottom: 16, boxShadow: SHADOWS.sm },
  sectionTitle:{ fontSize: 14, fontWeight: 700, color: COLORS.textMid, margin: '0 0 14px' },
  statusGrid:  { display: 'flex', flexWrap: 'wrap', gap: 12 },
  statusPill:  { textAlign: 'center', padding: '12px 20px', background: COLORS.surfaceHover, borderRadius: 8, minWidth: 80 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:          { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', borderBottom: `2px solid ${COLORS.border}`, whiteSpace: 'nowrap' },
  td:          { padding: '10px 12px', color: COLORS.textDark, whiteSpace: 'nowrap' },
};
